// A Postiz create whose response was lost must never be folded into a bulk action.
// `unknown` first needs a human reconciliation; `confirmed_absent` then gets one
// deliberate click on that channel's own retry button. Keeping this predicate
// shared makes the reviewer affordance and the request payload agree.
export const requiresDedicatedPostizAction = channel =>
    channel &&
    (channel.postizOutcome === 'unknown' || channel.postizOutcome === 'confirmed_absent');

/**
 * Is this channel's send already under way? The server leaves a requested channel
 * at `pending` and moves the POST to `publishing`, and the publish spine's
 * PRE-CREATE FENCE parks it at `failed` + postizOutcome `unknown` for the moment
 * around the Postiz create — so neither status alone shows a send in progress.
 * (`publishing` on the channel is the client's own optimistic flip.)
 *
 * Shared so the reviewer's state pill and the bulk plan agree on what "in flight"
 * means: a channel already being sent must neither offer a publish button nor be
 * counted into "Alle posten".
 *
 * @param {Object} channel
 * @param {?{status?:string}} post
 * @returns {boolean}
 */
export const isChannelInFlight = (channel, post) => {
    if (!channel) return false;
    if (channel.status === 'publishing') return true;
    if (!post || post.status !== 'publishing' || !channel.publishRequestedAt) return false;
    if (channel.status === 'pending') return true;
    return channel.status === 'failed' && channel.postizOutcome === 'unknown';
};

// A channel whose image never rendered cannot be sent; it is re-rendered or
// regenerated on its own first. Counted into "Alle posten", it made the server
// refuse the whole batch, every other channel with it.
const missesItsImage = channel =>
    Boolean((channel.imageExpected === true || channel.imageSpec) && !channel.renderedImageRef);

export const isBulkPublishableChannel = channel =>
    Boolean(
        channel &&
            !missesItsImage(channel) &&
            channel.status !== 'published' &&
            channel.status !== 'skipped' &&
            channel.status !== 'accepted' &&
            channel.platform !== 'facebook_group' &&
            channel.postizOutcome !== 'accepted' &&
            !requiresDedicatedPostizAction(channel)
    );
